// Programs that run inside the hero terminal.
//
// A shell that only prints is a prop. These take the screen, run at their own
// frame rate, and hand it back when you quit. Each one is a factory: it is
// given the grid the terminal measured for it and returns a `step()` that
// draws one frame, plus an optional `key()` for the ones you can play.
//
// Frames are plain strings. One text node per frame keeps a 20fps redraw off
// React's reconciler, and colour comes from the class the program declares
// rather than from a span around every character.

// ------------------------------------------------------------------ boot
// The stream kind: it writes into the transcript instead of taking the screen.
export function bootScript(stats) {
  const ok = (text) => ({ text: `[  OK  ] ${text}`, tone: 'ok' });
  return [
    { text: 'Oxide 4.0.0 (aarch64) booting', tone: 'hot' },
    ok('Mounted /proc, /sys, /dev'),
    ok(`Brought up ${stats.crates} crates`),
    ok('Started virtio block, net and console'),
    ok(`Registered ${stats.syscalls} syscalls`),
    ok('Reached target Multi-User System'),
    { text: '', tone: 'out' },
  ];
}

// ---------------------------------------------------------------- matrix
export function matrix({ cols, rows }) {
  const GLYPHS = 'abcdef0123456789!<>*+=/\\|{}[]:;';
  const drops = Array.from({ length: cols }, () => ({
    y: Math.random() * -rows,
    speed: 0.35 + Math.random() * 0.95,
    len: 4 + Math.floor(Math.random() * 11),
  }));

  return {
    label: 'matrix',
    paint: 'matrix',
    fps: 18,
    step() {
      const grid = Array.from({ length: rows }, () => new Array(cols).fill(' '));
      drops.forEach((drop, x) => {
        drop.y += drop.speed;
        if (drop.y - drop.len > rows) {
          drop.y = -Math.random() * 10;
          drop.speed = 0.35 + Math.random() * 0.95;
          drop.len = 4 + Math.floor(Math.random() * 11);
        }
        for (let back = 0; back < drop.len; back += 1) {
          const y = Math.floor(drop.y) - back;
          if (y >= 0 && y < rows) grid[y][x] = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        }
      });
      return grid.map((row) => row.join('')).join('\n');
    },
  };
}

// ----------------------------------------------------------------- donut
// The one everybody recognises: a torus rotated on two axes, lit by a fixed
// light and resolved onto the character grid through a z-buffer.
export function donut({ cols, rows }) {
  const SHADES = '.,-~:;=!*#$@';
  let a = 0;
  let b = 0;

  return {
    label: 'donut',
    paint: 'rust',
    fps: 24,
    step() {
      const output = new Array(cols * rows).fill(' ');
      const depth = new Array(cols * rows).fill(0);
      // Character cells are about twice as tall as they are wide, so the
      // horizontal scale is doubled or the torus comes out as an egg.
      // Projection scale. One number drives both axes, halved vertically for
      // the aspect of a character cell, and taken as whichever of the two
      // dimensions runs out first so the torus never draws past the grid.
      const scale = Math.min(cols * 0.42, rows * 1.5);
      const scaleX = scale;
      const scaleY = scale / 2;

      for (let theta = 0; theta < 6.28; theta += 0.09) {
        const [ct, st] = [Math.cos(theta), Math.sin(theta)];
        for (let phi = 0; phi < 6.28; phi += 0.025) {
          const [cp, sp] = [Math.cos(phi), Math.sin(phi)];
          const [ca, sa, cb, sb] = [Math.cos(a), Math.sin(a), Math.cos(b), Math.sin(b)];

          const circleX = 2 + ct;
          const inverseZ = 1 / (sp * circleX * sa + st * ca + 5);
          const t = sp * circleX * ca - st * sa;

          const x = Math.round(cols / 2 + scaleX * inverseZ * (cp * circleX * cb - t * sb));
          const y = Math.round(rows / 2 - scaleY * inverseZ * (cp * circleX * sb + t * cb));
          const at = x + cols * y;

          const light = cp * ct * sb - ca * ct * sp - sa * st + cb * (ca * st - ct * sa * sp);
          if (y < 0 || y >= rows || x < 0 || x >= cols || inverseZ <= depth[at] || light <= 0) continue;
          depth[at] = inverseZ;
          output[at] = SHADES[Math.floor(light * 8)] ?? '@';
        }
      }

      a += 0.07;
      b += 0.03;
      const lines = [];
      for (let row = 0; row < rows; row += 1) lines.push(output.slice(row * cols, (row + 1) * cols).join(''));
      return lines.join('\n');
    },
  };
}

// ------------------------------------------------------------------- top
// A task monitor over the workspace's own subsystems. The load is invented;
// the names, and the weight each one carries, are not.
export function top({ cols, rows }, tasks) {
  const width = Math.max(10, Math.min(28, cols - 30));
  const procs = tasks.slice(0, Math.max(4, rows - 5)).map((task, index) => ({
    pid: 100 + index * 7 + (index % 3),
    name: task.name,
    weight: task.weight,
    phase: Math.random() * 6.28,
  }));
  let tick = 0;

  return {
    label: 'top',
    paint: 'plain',
    fps: 8,
    step() {
      tick += 1;
      const rendered = procs.map((proc) => {
        const wave = (Math.sin(tick * 0.18 + proc.phase) + 1) / 2;
        const cpu = Math.min(99.9, proc.weight * (0.35 + wave * 0.75) + Math.random() * 3);
        return { ...proc, cpu };
      }).sort((one, two) => two.cpu - one.cpu);

      const total = rendered.reduce((sum, proc) => sum + proc.cpu, 0);
      const lines = [
        `oxide top   tasks ${procs.length}   load ${(total / 100).toFixed(2)}   up 102d`,
        '',
        `  PID  ${'COMMAND'.padEnd(16)}  CPU%  `,
      ];
      for (const proc of rendered) {
        const filled = Math.round((proc.cpu / 100) * width);
        lines.push(`${String(proc.pid).padStart(5)}  ${proc.name.slice(0, 16).padEnd(16)} ${proc.cpu.toFixed(1).padStart(5)}  ${'█'.repeat(filled)}${'·'.repeat(width - filled)}`);
      }
      return lines.join('\n');
    },
  };
}

// ----------------------------------------------------------------- snake
export function snake({ cols, rows }) {
  // Two characters per cell. A character is about half as wide as it is tall,
  // so a one-character cell makes a board that is twice as tall as it looks
  // and a snake that moves at two different speeds depending on the axis.
  const width = Math.floor(cols / 2);
  const height = Math.max(6, rows - 2);
  let body = [{ x: Math.floor(width / 2), y: Math.floor(height / 2) }];
  let dir = { x: 1, y: 0 };
  let queued = dir;
  let food = { x: 0, y: 0 };
  let score = 0;
  let dead = false;
  let best = 0;

  const drop = () => {
    do {
      food = { x: Math.floor(Math.random() * width), y: Math.floor(Math.random() * height) };
    } while (body.some((cell) => cell.x === food.x && cell.y === food.y));
  };
  drop();

  const restart = () => {
    body = [{ x: Math.floor(width / 2), y: Math.floor(height / 2) }];
    dir = { x: 1, y: 0 };
    queued = dir;
    score = 0;
    dead = false;
    drop();
  };

  return {
    label: 'snake',
    paint: 'game',
    fps: 9,
    // Arrows and wasd. The queue is what stops a fast double-tap from folding
    // the snake back through its own neck between two frames.
    key(name) {
      const moves = {
        ArrowUp: { x: 0, y: -1 }, w: { x: 0, y: -1 },
        ArrowDown: { x: 0, y: 1 }, s: { x: 0, y: 1 },
        ArrowLeft: { x: -1, y: 0 }, a: { x: -1, y: 0 },
        ArrowRight: { x: 1, y: 0 }, d: { x: 1, y: 0 },
      };
      if (name === ' ' && dead) { restart(); return 'handled'; }
      const move = moves[name];
      if (!move) return undefined;
      if (move.x === -dir.x && move.y === -dir.y) return 'handled';
      queued = move;
      return 'handled';
    },
    step() {
      if (!dead) {
        dir = queued;
        const head = { x: body[0].x + dir.x, y: body[0].y + dir.y };
        const hitWall = head.x < 0 || head.y < 0 || head.x >= width || head.y >= height;
        const hitSelf = body.some((cell) => cell.x === head.x && cell.y === head.y);
        if (hitWall || hitSelf) {
          dead = true;
          best = Math.max(best, score);
        } else {
          body.unshift(head);
          if (head.x === food.x && head.y === food.y) { score += 1; drop(); }
          else body.pop();
        }
      }

      const grid = Array.from({ length: height }, () => new Array(width).fill('  '));
      grid[food.y][food.x] = ' ◆';
      body.forEach((cell, index) => { grid[cell.y][cell.x] = index === 0 ? '▓▓' : '██'; });

      const status = dead
        ? `  dead. score ${score}, best ${best}. space to play again, q to quit`
        : `  score ${score}   best ${best}   arrows or wasd to steer, q to quit`;
      return [status, '', ...grid.map((row) => row.join(''))].join('\n');
    },
  };
}
